/**
 * What is still open for the reader of a review decision: the attestations
 * reviewers left on blockers they accepted, and the readiness blockers the
 * decision at hand has to account for.
 *
 * @module components/governance/ReviewOutstanding
 */

import React from "react";

import { ReadinessBlockerList } from "@/components/shared/ReadinessBlockerList";
import { Checkbox, countLabel, Text } from "@/components/ui";
import type { ProgrammeReviewOutcome, ReadinessBlocker } from "@/server/types";

import {
  type GovernanceRequirementsProps,
  readinessBlockerEntries,
} from "./GovernanceRequirements";

export interface ReviewOutstandingProps {
  /** Review outcomes that accepted open blockers, shown to the approver. */
  attestations: readonly ProgrammeReviewOutcome[];
  /** A user's name, from their id. */
  nameFor: (userId: string) => string;
  /** Blockers still open at QA approval. */
  approvalBlockers: readonly ReadinessBlocker[];
  /** Blockers a reviewer must attest to before marking reviewed. */
  reviewBlockers: readonly ReadinessBlocker[];
  navigationItems: GovernanceRequirementsProps["navigationItems"];
  hrefFor: GovernanceRequirementsProps["hrefFor"];
  attested: boolean;
  onAttestedChange: (attested: boolean) => void;
  disabled: boolean;
}

export function ReviewOutstanding({
  attestations,
  nameFor,
  approvalBlockers,
  reviewBlockers,
  navigationItems,
  hrefFor,
  attested,
  onAttestedChange,
  disabled,
}: ReviewOutstandingProps): React.ReactElement {
  return (
    <Text as="div">
      {attestations.length > 0 ? (
        <ul className="mt-2 space-y-2" data-testid="review-decision-attestations">
          {attestations.map((outcome) => (
            <li key={outcome.id} data-testid={`review-decision-attestation-${outcome.id}`}>
              <span className="font-semibold">
                {nameFor(outcome.reviewerId)} marked reviewed with{" "}
                {countLabel(outcome.openBlockerCount ?? 0, "open blocker")}:
              </span>{" "}
              <span className="whitespace-pre-line">{outcome.note}</span>
            </li>
          ))}
        </ul>
      ) : null}
      {approvalBlockers.length > 0 ? (
        <div className="mt-2" data-testid="review-decision-blockers">
          <p>{countLabel(approvalBlockers.length, "readiness blocker")} still open:</p>
          <ReadinessBlockerList
            entries={readinessBlockerEntries(approvalBlockers, navigationItems, hrefFor)}
            testIdPrefix="review-decision-blocker"
          />
        </div>
      ) : null}
      {reviewBlockers.length > 0 ? (
        <div className="mt-2" data-testid="review-decision-blockers">
          <p>
            {countLabel(reviewBlockers.length, "readiness blocker")} still open. Marking reviewed
            accepts them, so your comment must say why.
          </p>
          <ReadinessBlockerList
            entries={readinessBlockerEntries(reviewBlockers, navigationItems, hrefFor)}
            testIdPrefix="review-decision-blocker"
          />
          <label className="mt-2 flex items-start gap-3" htmlFor="review-attestation">
            <Checkbox
              id="review-attestation"
              checked={attested}
              onChange={onAttestedChange}
              disabled={disabled}
              data-testid="review-attestation"
            />
            <span>
              My comment addresses the {countLabel(reviewBlockers.length, "open blocker")}, and is
              recorded as my attestation for the approver.
            </span>
          </label>
        </div>
      ) : null}
    </Text>
  );
}
